import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFilter, FiDownload, FiFileText, FiUser, FiUpload, FiEdit, FiTrash2, FiEye, FiMessageSquare, FiCheckCircle, FiClock, FiSearch, FiChevronDown, FiShield, FiUserPlus } = FiIcons;

const mockActivities = [
  {
    id: 1,
    type: 'document_upload',
    user: 'Dr. Mike Davis',
    action: 'uploaded document',
    target: 'CBCT Scan - Initial Assessment.pdf',
    timestamp: '2024-01-15 14:30:15',
    ip: '192.168.1.100',
    metadata: { size: '2.4 MB', version: 'v1.0' }
  },
  {
    id: 2,
    type: 'document_view',
    user: 'Dr. Sarah Smith',
    action: 'viewed document',
    target: 'Treatment Plan - Implacement Protocol.pdf',
    timestamp: '2024-01-15 13:45:22',
    ip: '192.168.1.101',
    metadata: { duration: '5 minutes 32 seconds' }
  },
  {
    id: 3,
    type: 'message_sent',
    user: 'Dr. Lisa Wilson',
    action: 'sent encrypted message',
    target: 'Discussion thread: Abutment Design',
    timestamp: '2024-01-15 12:15:08',
    ip: '192.168.1.102',
    metadata: { encrypted: true, priority: 'normal' }
  },
  {
    id: 4,
    type: 'participant_invited',
    user: 'Dr. Sarah Smith',
    action: 'invited participant',
    target: 'Dr. Robert Johnson (Oral Surgeon)',
    timestamp: '2024-01-15 11:30:45',
    ip: '192.168.1.100',
    metadata: { role: 'specialist' }
  },
  {
    id: 5,
    type: 'document_approved',
    user: 'Dr. Sarah Smith',
    action: 'approved document',
    target: 'Patient Consent Form.pdf',
    timestamp: '2024-01-15 10:20:33',
    ip: '192.168.1.100',
    metadata: { previous_status: 'review' }
  },
  {
    id: 6,
    type: 'login',
    user: 'John Doe (Patient)',
    action: 'logged in',
    target: 'Patient portal access',
    timestamp: '2024-01-15 09:45:12',
    ip: '192.168.1.200',
    metadata: { mfa_verified: true }
  },
  {
    id: 7,
    type: 'document_edit',
    user: 'Dr. Mike Davis',
    action: 'edited document',
    target: 'Treatment Plan - Implacement Protocol.pdf',
    timestamp: '2024-01-14 16:30:28',
    ip: '192.168.1.100',
    metadata: { changes: 'Updated implant specifications', version: 'v2.1' }
  },
  {
    id: 8,
    type: 'document_delete',
    user: 'Dr. Sarah Smith',
    action: 'deleted document',
    target: 'Old_XRay_2023.jpg',
    timestamp: '2024-01-14 15:15:44',
    ip: '192.168.1.101',
    metadata: { reason: 'Outdated imaging' }
  }
];

const activityTypes = [
  { id: 'all', label: 'All Activities', icon: FiFileText },
  { id: 'document', label: 'Documents', icon: FiFileText },
  { id: 'message', label: 'Messages', icon: FiMessageSquare },
  { id: 'user', label: 'User Actions', icon: FiUser }
];

const activityIcons = {
  document_upload: FiUpload,
  document_view: FiEye,
  document_edit: FiEdit,
  document_delete: FiTrash2,
  document_approved: FiCheckCircle,
  message_sent: FiMessageSquare,
  participant_invited: FiUserPlus,
  login: FiUser,
  logout: FiUser
};

const ActivityLog = ({ dealId }) => {
  const [activities, setActivities] = useState(mockActivities);
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         activity.target.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || 
                         activity.type.startsWith(selectedType) ||
                         (selectedType === 'document' && activity.type.includes('document'));
    
    return matchesSearch && matchesType;
  });

  const getActivityIcon = (type) => {
    if (type.includes('document')) return activityIcons.document_upload;
    if (type.includes('message')) return activityIcons.message_sent;
    if (type.includes('participant')) return activityIcons.participant_invited;
    if (type.includes('login') || type.includes('logout')) return activityIcons.login;
    return activityIcons.document_upload;
  };

  const getActivityColor = (type) => {
    if (type.includes('upload') || type.includes('create')) return 'text-green-600 bg-green-50';
    if (type.includes('delete')) return 'text-red-600 bg-red-50';
    if (type.includes('edit')) return 'text-blue-600 bg-blue-50';
    if (type.includes('view')) return 'text-gray-600 bg-gray-50';
    if (type.includes('approve')) return 'text-purple-600 bg-purple-50';
    return 'text-gray-600 bg-gray-50';
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Target', 'IP Address', 'Metadata'],
      ...filteredActivities.map(activity => [
        activity.timestamp,
        activity.user,
        activity.action,
        activity.target,
        activity.ip,
        JSON.stringify(activity.metadata)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dealroom-activity-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const stats = {
    totalActivities: activities.length,
    activeUsers: new Set(activities.map(a => a.user)).size,
    documentActivities: activities.filter(a => a.type.includes('document')).length,
    complianceScore: 98
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <SafeIcon icon={FiFileText} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-700">Total Activities</p>
              <p className="text-2xl font-bold text-blue-900">{stats.totalActivities}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <SafeIcon icon={FiUser} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700">Active Users</p>
              <p className="text-2xl font-bold text-green-900">{stats.activeUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm border border-purple-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <SafeIcon icon={FiUpload} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-700">Document Activities</p>
              <p className="text-2xl font-bold text-purple-900">{stats.documentActivities}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700">Compliance Score</p>
              <p className="text-2xl font-bold text-green-900">{stats.complianceScore}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <SafeIcon icon={FiDownload} className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <SafeIcon icon={FiFilter} className="h-4 w-4" />
              Filters
              <SafeIcon icon={FiChevronDown} className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activity Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  >
                    {activityTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Recent Activities ({filteredActivities.length})
            </h3>
            <p className="text-sm text-gray-600">
              Last 24 hours: {filteredActivities.filter(a => {
                const activityDate = new Date(a.timestamp);
                const now = new Date();
                const hoursDiff = (now - activityDate) / (1000 * 60 * 60);
                return hoursDiff <= 24;
              }).length} activities
            </p>
          </div>
        </div>

        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          <AnimatePresence>
            {filteredActivities.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${getActivityColor(activity.type)}`}>
                    <SafeIcon icon={getActivityIcon(activity.type)} className="h-4 w-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{activity.user}</span>
                      <span className="text-gray-600">{activity.action}</span>
                    </div>
                    
                    <p className="text-sm text-gray-900 font-medium truncate">{activity.target}</p>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiClock} className="h-3 w-3" />
                        {activity.timestamp}
                      </span>
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiUser} className="h-3 w-3" />
                        IP: {activity.ip}
                      </span>
                    </div>

                    {activity.metadata && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(activity.metadata).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {key}: {String(value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredActivities.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <SafeIcon icon={FiSearch} className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No activities found matching your filters.</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiShield} className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-gray-900">HIPAA Compliant Logging</p>
              <p className="text-sm text-gray-600">All activities are encrypted and stored in SOC 2 certified servers</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Data retention: 7 years</p>
            <p className="text-sm text-gray-600">Last backup: 2 hours ago</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;